import React, { useEffect, useState } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid'

import { InfoTooltip } from '../../components/InfoTooltip/InfoTooltip'
import { useCreateSettlementVirtualAccount } from '../../hooks'
import styles from './DepositDefaultModal.module.scss'

const formatCurrency = (value) => {
	if (value === null || value === undefined) return '-'
	const number = Number(value)
	return Number.isNaN(number) ? value : number.toLocaleString()
}

const formatDeadline = (value) => {
	if (!value) return '-'

	// 서버 응답 형식이 YYYYMMDDHHmmss 또는 YYYY-MM-DD 등의 문자열일 수 있으므로 처리
	const normalizeDate = (raw) => {
		if (!raw) return null

		// 이미 Date 생성 가능한 형태면 바로 사용
		const parsed = new Date(raw)
		if (!Number.isNaN(parsed.getTime())) {
			return parsed
		}

		// 14자리 숫자 문자열 (YYYYMMDDHHmmss) 처리
		if (typeof raw === 'string' && raw.length === 14) {
			const year = Number(raw.slice(0, 4))
			const month = Number(raw.slice(4, 6)) - 1
			const day = Number(raw.slice(6, 8))
			const hour = Number(raw.slice(8, 10))
			const minute = Number(raw.slice(10, 12))
			const second = Number(raw.slice(12, 14))
			return new Date(year, month, day, hour, minute, second)
		}

		return null
	}

	const date = normalizeDate(value)
	if (!date) return '-'

	const dayNames = ['일', '월', '화', '수', '목', '금', '토']
	const formattedDate = `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(2, '0')}월 ${String(date.getDate()).padStart(2, '0')}일`
	const dayLabel = dayNames[date.getDay()]

	return `${formattedDate}(${dayLabel})까지`
}

const DepositDefaultModal = ({ item, onClose }) => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false)
	const [selectedBank, setSelectedBank] = useState(null)

	const [depositDeadline, setDepositDeadline] = useState('-')
	const [depositAccountNumber, setDepositAccountNumber] = useState('-')
	const [depositAmount, setDepositAmount] = useState('-')
	const [depositAmountDetails, setDepositAmountDetails] = useState({
		base: '-',
		withholding: '-',
	})
	const [pageIndex, setPageIndex] = useState(1)
	const [isWithholdingTaxChecked, setIsWithholdingTaxChecked] = useState(false)
	const { mutateAsync: createVirtualAccount, isPending: isCreatingVirtualAccount } =
		useCreateSettlementVirtualAccount()

	// 모달이 마운트될 때 body 스크롤 막기
	useEffect(() => {
		document.body.style.overflow = 'hidden'

		// 컴포넌트가 언마운트될 때 body 스크롤 복원
		return () => {
			document.body.style.overflow = 'unset'
		}
	}, [])

	const createDepositAccountNumber = async () => {
		if (!selectedBank?.code || !item?.id) {
			console.warn('Missing data for virtual account request:', {
				bankCode: selectedBank?.code,
				contractPaymentId: item?.id,
			})
			return
		}

		const payload = {
			bankId: selectedBank.code,
			contractPaymentId: item.id,
			withholdingTaxPaid: isWithholdingTaxChecked,
		}

		try {
			const response = await createVirtualAccount(payload)

			if (response) {
				const {
					bankAccountNumber,
					expireDate,
					totalAmount,
					amountWithoutTax,
					withholdingTaxAmount,
					amount,
				} = response

				setDepositAccountNumber(bankAccountNumber ?? '-')
				setDepositDeadline(formatDeadline(expireDate))

				const displayAmount =
					totalAmount ?? amount ?? item?.contractPayment?.proTotalPayment ?? null
				setDepositAmount(formatCurrency(displayAmount))

				setDepositAmountDetails({
					base: formatCurrency(amountWithoutTax),
					withholding: formatCurrency(withholdingTaxAmount),
				})
			}
		} catch (error) {
			console.log(error)
			setDepositAccountNumber('-')
			setDepositDeadline('-')
			setDepositAmount('-')
			setDepositAmountDetails({
				base: '-',
				withholding: '-',
			})
		}
	}

	const handleItemClick = async (bank) => {
		setSelectedBank(bank)
		setIsDropdownOpen(false)
	}

	const handleNextButtonClick = async () => {
		setPageIndex(2)
		await createDepositAccountNumber()
	}

	const handleConfirmButtonClick = () => {
		onClose()
		console.log('handleConfirmButtonClick')
	}

	const bankList = [
		{ name: 'IBK기업은행', value: 'IBK기업은행', code: '003' },
		{ name: 'KB국민은행', value: 'KB국민은행', code: '004' },
		{ name: 'NH농협은행', value: 'NH농협은행', code: '011' },
		{ name: '우리은행', value: '우리은행', code: '020' },
		{ name: 'SC제일은행', value: 'SC제일은행', code: '023' },
		{ name: 'iM뱅크(대구은행)', value: 'iM뱅크(대구은행)', code: '031' },
		{ name: '부산은행', value: '부산은행', code: '032' },
		{ name: '광주은행', value: '광주은행', code: '034' },
		{ name: '경남은행', value: '경남은행', code: '045' },
		{ name: '새마을금고', value: '새마을금고', code: '039' },
		{ name: '우체국', value: '우체국', code: '071' },
		{ name: '하나은행(KEB하나은행)', value: '하나은행(KEB하나은행)', code: '081' },
		{ name: '신한은행', value: '신한은행', code: '088' },
		{ name: '케이뱅크', value: '케이뱅크', code: '089' },
	]

	return (
		<div className={styles.depositDefaultModalOverlay}>
			<div className={styles.depositDefaultModalContainer}>
				{pageIndex === 1 && (
					<>
						<div className={styles.depositDefaultModalTitle}>입금하기</div>
						<div className={styles.selectBankContainer}>
							<div className={styles.selectBankTitle}>입금 은행</div>
							<div
								className={styles.bankDropdownContainer}
								onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
								<div className={styles.selectedBankName}>
									{selectedBank ? selectedBank.name : '은행을 선택해주세요.'}
								</div>
								{!isDropdownOpen && (
									<ChevronDownIcon className={styles.chevronDownIcon} />
								)}
								{isDropdownOpen && (
									<ChevronUpIcon className={styles.chevronUpIcon} />
								)}
								{isDropdownOpen && (
									<div className={styles.bankDropdownList}>
										{bankList.map((bank) => (
											<div
												key={bank.code}
												className={styles.bankDropdownItem}
												onClick={() => handleItemClick(bank)}>
												{bank.name}
											</div>
										))}
									</div>
								)}
							</div>
						</div>
						<div className={styles.withholdingRow}>
							<div>
								<span className={styles.withholdingLabel}>원천세 대납 신청</span>
								<InfoTooltip
									id="withholding-tax-tooltip"
									content="원천세 대납 신청 시 스포이음에서 원천세를 대리 수령하여 납부해 드립니다."
									placement="top"
								/>
							</div>
							<input
								type="checkbox"
								className={styles.withholdingCheckbox}
								checked={isWithholdingTaxChecked}
								onChange={(event) =>
									setIsWithholdingTaxChecked(event.target.checked)
								}
							/>
						</div>
						<div className={styles.buttonContainer}>
							<div className={styles.cancelButton} onClick={onClose}>
								취소
							</div>
							<div
								onClick={() => handleNextButtonClick()}
								disabled={!selectedBank || isCreatingVirtualAccount}
								className={`${styles.depositButton} ${
									!selectedBank ? styles.depositButtonDisabled : ''
								}`}>
								다음
							</div>
						</div>
					</>
				)}
				{pageIndex === 2 && (
					<>
						<div className={styles.depositDefaultModalTitle}>입금하기</div>
						{/* <div className={styles.selectBankContainer}>
							<div className={styles.selectBankTitle}>입금 은행</div>
							<div
								className={styles.bankDropdownContainer}
								onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
								<div className={styles.selectedBankName}>{selectedBank}</div>
								{!isDropdownOpen && (
									<ChevronDownIcon className={styles.chevronDownIcon} />
								)}
								{isDropdownOpen && (
									<ChevronUpIcon className={styles.chevronUpIcon} />
								)}
								{isDropdownOpen && (
									<div className={styles.bankDropdownList}>
										{bankList.map((bank) => (
											<div
												key={bank.code}
												className={styles.bankDropdownItem}
												onClick={() => handleItemClick(bank)}>
												{bank.name}
											</div>
										))}
									</div>
								)}
							</div>
						</div> */}
						<div className={styles.infoRow}>
							<span className={styles.infoRowTitle}>입금 은행</span>
							<span className={styles.infoRowValue}>
								{selectedBank ? selectedBank.name : '-'}
							</span>
						</div>
						<div className={styles.infoRow}>
							<span className={styles.infoRowTitle}>입금 계좌</span>
							<span className={styles.infoRowValue}>{depositAccountNumber}</span>
						</div>

						<div className={styles.infoRow}>
							<span className={styles.infoRowTitle}>입금 기한</span>
							<span className={styles.infoRowValue}>{depositDeadline}</span>
						</div>
						<div className={styles.infoRow}>
							<span className={styles.infoRowTitle}>원천세 대납 서비스</span>
							<span className={styles.infoRowValue}>
								{isWithholdingTaxChecked ? '서비스 이용' : '서비스 이용안함'}
							</span>
						</div>
						{isWithholdingTaxChecked && (
							<div style={{ marginTop: '2px' }}>
								<div className={styles.infoSubRow}>
									<span>기본 금액</span>
									<span>{depositAmountDetails.base}원</span>
								</div>
								<div className={styles.infoSubRow}>
									<span>원천세</span>
									<span>{depositAmountDetails.withholding}원</span>
								</div>
							</div>
						)}
						<div className={styles.infoRow}>
							<span className={styles.infoRowTitle}>입금 금액</span>
							<span className={styles.infoRowValue}>{depositAmount}원</span>
						</div>

						<div className={styles.depositDetail}>
							위 기한 내에 입금이 완료되지 않으면 <br />
							서비스 이용에 제한이 있을 수 있습니다.
						</div>
						<div
							className={`${styles.buttonContainer} ${styles.buttonContainerSingle}`}>
							<button
								type="button"
								disabled={isCreatingVirtualAccount}
								onClick={() => handleConfirmButtonClick()}
								className={styles.depositButton}>
								확인
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	)
}

export default DepositDefaultModal
